import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import List from '@pelckmans/elementis/components/list';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { Medialink } from '../../api/medialink';
import type { SlideSet } from '../../api/slide-set';
import type { UserMaterial } from '../../api/user-material';
import ModalContext from '../../contexts/ModalContext';
import { MaterialIcon } from '../../pages/material/components/MaterialIcon';
import MaterialPills from '../../pages/material/toc/components/MaterialPills';
import { groupByContentType } from '../../pages/material/toc/components/utils';

const BASE = 'PAGES.TOC_LEVEL.MODALS.SHARE_SELECTION';

interface ShareSelectionModalProps {
  id: string;
  userMaterials?: UserMaterial[];
  slideSets?: SlideSet[];
  medialinks?: Medialink[];
}

const getPillGroupProps = (materialPills: React.ReactNode) => ({
  role: 'group',
  'aria-label': undefined,
  children: materialPills,
});

const UserMaterialListItem = ({ userMaterial }: { userMaterial: UserMaterial }) => (
  <List.Item
    title={userMaterial.name}
    icon={<MaterialIcon materialType="user-material" />}
    pillGroupProps={getPillGroupProps(
      <MaterialPills isShared={userMaterial.shared} isAssignment={!!userMaterial.assignment} />,
    )}
  />
);

const SlideSetListItem = ({ slideSet }: { slideSet: SlideSet }) => (
  <List.Item
    title={slideSet.name}
    icon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
    pillGroupProps={getPillGroupProps(<MaterialPills isShared={slideSet.shared} />)}
  />
);

const MedialinkListItem = ({ medialink }: { medialink: Medialink }) => (
  <List.Item
    title={medialink.name}
    icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
    pillGroupProps={getPillGroupProps(
      <MaterialPills
        isAssignment={medialink.assignments.length > 0}
        isShared={medialink.shares.length > 0}
        isStudent={medialink.group === 'student'}
        isTeacher={medialink.group === 'teacher'}
        isAdaptive={medialink.assessmentQExercise?.isAdaptive}
        isOnTheFly={medialink.assessmentQExercise?.isOnTheFly}
      />,
    )}
  />
);

export default function ShareSelectionModal({ id, userMaterials, slideSets, medialinks }: ShareSelectionModalProps) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  const totalSelected = (userMaterials?.length || 0) + (slideSets?.length || 0) + (medialinks?.length || 0);
  if (totalSelected === 0) return null;

  const handleClose = () => closeModal(id, true);

  const groupedMedialinks = groupByContentType(medialinks ? medialinks : []);

  return (
    <Dialog id={id} modal size="md" onClose={handleClose} aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header
        title={
          totalSelected === 1
            ? t(`${BASE}.HEADER.TITLE_SINGLE`, {
                name: userMaterials?.[0]?.name || slideSets?.[0]?.name || medialinks?.[0]?.name,
              })
            : t(`${BASE}.HEADER.TITLE_MULTIPLE`, { count: totalSelected })
        }
        onClose={handleClose}
        aria-label={t(`${BASE}.HEADER.A11Y_LABEL`)}
      />
      <Dialog.Body>
        <List>
          {userMaterials?.map(material => (
            <UserMaterialListItem userMaterial={material} key={material.id} />
          ))}
          {slideSets?.map(slideSet => (
            <SlideSetListItem slideSet={slideSet} key={`${slideSet.id}-${slideSet.versionId}`} />
          ))}
          {Object.keys(groupedMedialinks).map(category =>
            groupedMedialinks[category].map(medialink => (
              <MedialinkListItem medialink={medialink} key={medialink.id} />
            )),
          )}
        </List>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <Button prominence="neutral" onClick={handleClose} key="close">
              {t('ACTIONS.CLOSE')}
            </Button>
          ),
        }}
      ></Dialog.Footer>
    </Dialog>
  );
}
